import { useState, useEffect, useRef } from 'react';
import './Profissionais.css';
import { profissionaisData, WHATSAPP_NUMBER } from '../../constants';

function Profissionais() {
  const [selectedProfissional, setSelectedProfissional] = useState(null);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [animatePhotos, setAnimatePhotos] = useState(false);
  const sectionRef = useRef(null);

  // Separar profissionais em destaque e demais
  const profissionaisDestaque = profissionaisData.filter(prof => prof.destaque);
  const demaisProfissionais = profissionaisData.filter(prof => !prof.destaque);

  // Intersection Observer para animar as fotos quando a seção entrar na viewport
  useEffect(() => {
    const currentSection = sectionRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatePhotos(true);
          }
        });
      },
      {
        threshold: 0.1, // Ativa quando 10% da seção está visível
        rootMargin: '0px'
      }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []); // Executa apenas uma vez quando o componente monta

  const getWhatsAppMessage = (profissional) => {
    const artigo = profissional.genero === 'M' ? 'o' : 'a';
    const mensagem = `Olá, venho do site e gostaria de marcar consulta com ${artigo} ${profissional.nome}!`;
    return encodeURIComponent(mensagem);
  };

  const handleCardClick = (prof) => {
    setSelectedProfissional(prof);
  };

  const handleCloseModal = () => {
    setSelectedProfissional(null);
  };

  const toggleMostrarTodos = () => {
    if (mostrarTodos) {
      // Se está fechando, inicia a animação
      setIsClosing(true);
      setTimeout(() => {
        setMostrarTodos(false);
        setIsClosing(false);
      }, 300); // Tempo da animação de fechamento
    } else {
      // Se está abrindo, apenas abre
      setMostrarTodos(true);
    }
  };

  return (
    <section id="profissionais" className="profissionais-section" ref={sectionRef}>
      <div className="profissionais-container">
        <h2>Profissionais</h2>

        {/* Profissionais em Destaque */}
        <div className={`profissionais-grid ${animatePhotos ? 'animate' : ''}`}>
          {profissionaisDestaque.map((prof, index) => (
            <div
              key={index}
              className="profissional-card profissional-destaque"
              onClick={() => handleCardClick(prof)}
              style={{
                '--ordem-desktop': prof.ordemDesktop || index + 1,
                '--ordem-mobile': prof.ordemMobile || index + 1
              }}
            >
              <div className="profissional-foto">
                {prof.foto ? (
                  <div className="profissional-foto-wrapper">
                    <img src={prof.foto} alt={prof.nome} className="foto-profissional" />
                  </div>
                ) : (
                  <div className="foto-placeholder"></div>
                )}
              </div>
              <div className="profissional-background"></div>
              <div className="profissional-info">
                <h3>{prof.nome}</h3>
                <p>{prof.especialidade}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Botão Exibir Mais */}
        {demaisProfissionais.length > 0 && (
          <div className="exibir-mais-container">
            <button
              className={`exibir-mais-btn ${mostrarTodos ? 'ativo' : ''}`}
              onClick={toggleMostrarTodos}
            >
              {mostrarTodos ? 'Exibir Menos' : 'Exibir Mais'}
            </button>
          </div>
        )}

        {/* Demais Profissionais */}
        {(mostrarTodos || isClosing) && demaisProfissionais.length > 0 && (
          <div className={`demais-profissionais-grid ${isClosing ? 'hide' : 'show'}`}>
            {demaisProfissionais.map((prof, index) => (
              <div
                key={index}
                className="profissional-card profissional-card-pequeno"
                onClick={() => handleCardClick(prof)}
              >
                <div className="profissional-foto">
                  {prof.foto ? (
                    <div className="profissional-foto-wrapper">
                      <img src={prof.foto} alt={prof.nome} className="foto-profissional" />
                    </div>
                  ) : (
                    <div className="foto-placeholder"></div>
                  )}
                </div>
                <div className="profissional-info">
                  <h3>{prof.nome}</h3>
                  <p>{prof.especialidade}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal lateral */}
      {selectedProfissional && (
        <>
          <div className="modal-overlay" onClick={handleCloseModal}></div>
          <div className="modal-sidebar">
            <button className="modal-close" onClick={handleCloseModal}>×</button>
            <div className="modal-content">
              <div className="modal-foto">
                {selectedProfissional.foto ? (
                  <div className="profissional-foto-wrapper">
                    <img src={selectedProfissional.foto} alt={selectedProfissional.nome} className="foto-profissional" />
                  </div>
                ) : (
                  <div className="foto-placeholder"></div>
                )}
              </div>
              <h3>{selectedProfissional.nome}</h3>
              <p className="modal-especialidade">{selectedProfissional.especialidade}</p>
              <div className="modal-bio">
                <h4>Sobre</h4>
                {selectedProfissional.bio ? (
                  selectedProfissional.bio.split('\n\n').map((paragrafo, index) => (
                    <p key={index}>{paragrafo}</p>
                  ))
                ) : (
                  <>
                    <p>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </p>
                    <p>
                      Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                    </p>
                  </>
                )}
              </div>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${getWhatsAppMessage(selectedProfissional)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-agendar-btn"
              >
                Agendar Consulta
              </a>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default Profissionais;